import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useForm } from 'react-hook-form';

import Button, { ButtonSize, ButtonVariant } from 'components/Button';
import { useAppSelector, useAppDispatch } from 'hooks';
import { getOtpEnrollUrl, isSmartHubDomain } from 'utils/domainService';
import { LoginFormValues } from 'entities/login';
import { USER_WITH_OID } from 'services/persons';
import { Input } from 'components/Inputs';
import { getUrlParams } from 'utils/url';
import { loginRequest } from 'config';
import path from 'common/path';

import { getLoginData } from 'pages/login/selectors';
import { emailValidation } from 'pages/login/utils';
import { verifyPerson, notifyVerifyPersonFailed } from 'pages/login/thunks';
import { setData } from 'pages/login/loginSlice';

const EnrollForm = () => {
  const { t } = useTranslation(['enroll', 'login']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { instance } = useMsal();

  const urlParams: any = getUrlParams();

  const data = useAppSelector(getLoginData);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    defaultValues: {
      email: urlParams?.$email || data.email,
      phone: urlParams?.$phone || data.phone,
    },
  });

  const email = watch('email');

  const handleLogin = async () => {
    dispatch(
      setData({
        email,
      }),
    );
    navigate(path.login);
  };

  const handleFormSubmit = async (values: LoginFormValues) => {
    // the email code comes first: nothing is created until the code is right. A page on a smart-hub.com host
    // can't keep the session cookie, so there we only find the route (no code is sent) and send email-code
    // users to smart-enroll.com
    const onSmartHubHost = isSmartHubDomain();

    const loginData: LoginFormValues = {
      email: values.email,
      sendCode: !onSmartHubHost,
    };

    dispatch(setData(loginData));

    const result = await dispatch(verifyPerson(loginData));

    if (!verifyPerson.fulfilled.match(result)) {
      notifyVerifyPersonFailed(dispatch, result);
      return;
    }

    // ErrorFound: nothing to act on, so say so rather than leaving the form silent
    if (result.payload == null) {
      notifyVerifyPersonFailed(dispatch, null);
      return;
    }

    if (result.payload === USER_WITH_OID) {
      instance.loginRedirect(loginRequest);
      return;
    }

    if (onSmartHubHost) {
      window.location.assign(getOtpEnrollUrl(loginData));
    }

    // otherwise the code was sent and the login page shows the code form (isCodeSent)
    if (result.payload === 'sendCode') {
      navigate(path.login);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="py-8 px-4 md:px-8">
        <Input
          name="email"
          placeholder={t('form.email.placeholder')}
          label={t('form.email.label')}
          register={register}
          error={errors?.email}
          validation={emailValidation(t)}
        />

        <Button
          loading={isSubmitting}
          type="submit"
          className="my-6 w-full"
          heightClass="h-11"
          size={ButtonSize.mediumLarge}
        >
          {t('form.enroll')}
        </Button>

        <div className="flex justify-center items-center">
          <p className="text-secondary font-medium px-2.5">
            {t('form.existed')}
          </p>
          <Button
            onClick={handleLogin}
            variant={ButtonVariant.link}
            size={ButtonSize.medium}
            heightClass="h-6"
            paddingClass="px-2.5"
          >
            {t('form.signIn')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default EnrollForm;
